import { z } from 'zod'
import { hasControlChars, isIsoDate } from '../validators'

export const REPORT_FILTER_KEYS = [
  'portfolio',
  'finding',
  'theme',
  'country',
  'sector',
  'engagement_status',
  'query',
  'from',
  'to',
] as const
export type ReportFilterKey = (typeof REPORT_FILTER_KEYS)[number]

const filterValue = z
  .string()
  .max(180)
  .refine((v) => !hasControlChars(v), { message: 'Control characters are not permitted.' })

/**
 * Portfolio/report filters (RPT-002, PORT-006). Unknown keys are rejected,
 * empty strings dropped, `from`/`to` must be real dates with from <= to.
 */
export const reportFiltersSchema = z
  .strictObject({
    portfolio: filterValue.optional(),
    finding: filterValue.optional(),
    theme: filterValue.optional(),
    country: filterValue.optional(),
    sector: filterValue.optional(),
    engagement_status: filterValue.optional(),
    query: filterValue.optional(),
    from: filterValue.optional(),
    to: filterValue.optional(),
  })
  .transform((value) => {
    const out: Partial<Record<ReportFilterKey, string>> = {}
    for (const key of REPORT_FILTER_KEYS) {
      const v = value[key]
      if (v) out[key] = v
    }
    return out
  })
  .superRefine((value, ctx) => {
    for (const key of ['from', 'to'] as const) {
      const v = value[key]
      if (v !== undefined && !isIsoDate(v)) {
        ctx.addIssue({ code: 'custom', path: [key], message: 'Use YYYY-MM-DD dates.' })
      }
    }
    if (value.from && value.to && value.from > value.to) {
      ctx.addIssue({
        code: 'custom',
        path: ['from'],
        message: 'Start date must not follow end date.',
      })
    }
  })

export type ReportFilters = z.infer<typeof reportFiltersSchema>
